// Central RetrofitIQ image-branding pipeline.
// One reusable transformation so every PUBLIC image is delivered optimised
// (f_auto/q_auto) and branded with the official RetrofitIQ watermark (top-left,
// ~6% width, ~85% opacity). Hero images are excluded. The original asset is
// never modified — Cloudinary applies everything dynamically at delivery time.
const CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "";
const ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const WATERMARK = "l_retrofitiq:brand:watermark,w_0.08,fl_relative,g_north_west,x_24,y_24,o_95";

// Media Library (DAM) delivery URLs for the migrated local content images,
// keyed by their original web-path. Populated once from /api/content-images so
// the site serves these from Cloudinary (single source of truth) instead of the
// local physical files. Empty until loaded — brandedImage falls back gracefully.
let _contentImages = {};
export function setContentImages(map) {
  _contentImages = map || {};
}

// Normalise any src to the web-path key used by the DAM map.
function _webPathKey(src) {
  if (!src) return src;
  if (ORIGIN && src.startsWith(ORIGIN)) return src.slice(ORIGIN.length);
  return src;
}

// Cloudinary "fetch" delivery for remote (non-Cloudinary) images already used
// across the site. Returns the original URL untouched when branding can't apply.
export function brandedImage(src, { hero = false, width = 1600 } = {}) {
  if (!src || hero || !CLOUD) return src;
  if (src.startsWith("data:") || src.endsWith(".svg")) return src;
  // Migrated local content image — serve the Media Library (DAM) version.
  const mapped = _contentImages[_webPathKey(src)];
  if (mapped) return mapped.url;
  // Local/relative asset — resolve against the site origin so Cloudinary fetch can brand it.
  if (!/^https?:\/\//i.test(src)) {
    if (src.startsWith("/") && ORIGIN) src = ORIGIN + src;
    else return src;
  }
  // Already a Cloudinary-delivered URL from our backend (already watermarked) — leave as-is.
  if (src.includes("res.cloudinary.com")) return src;
  const base = `f_auto,q_auto,w_${width},c_limit`;
  return `https://res.cloudinary.com/${CLOUD}/image/fetch/${base}/${WATERMARK}/${encodeURIComponent(src)}`;
}

// Responsive srcset for a remote image branded via Cloudinary fetch.
export function brandedSrcSet(src, { hero = false, widths = [400, 800, 1200, 1600] } = {}) {
  if (!src || hero || !CLOUD) return undefined;
  const mapped = _contentImages[_webPathKey(src)];
  if (mapped) return mapped.srcset;
  if (src.includes("res.cloudinary.com")) return undefined;
  if (!/^https?:\/\//i.test(src) && !(src.startsWith("/") && ORIGIN)) return undefined;
  return widths.map((w) => `${brandedImage(src, { width: w })} ${w}w`).join(", ");
}

// One-time load of the DAM content-image map. Safe to call repeatedly.
export async function loadContentImages() {
  if (!ORIGIN) return {};
  try {
    const res = await fetch(`${ORIGIN}/api/content-images`);
    const data = await res.json();
    setContentImages(data.images || {});
    return _contentImages;
  } catch {
    return {};
  }
}
